import { Injectable } from '@angular/core';
import { StockQuantityService } from '../../services/stock-quantity.service';

/** Shown in place of an amount nobody ever set. Deliberately not '£0.00' and not a blank cell. */
export const NOT_PRICED = 'Not priced';

/**
 * Renders money for the cost screens, and is the one place that decides how a missing cost looks.
 *
 * The distinction it exists to keep: NULL means nobody has ever priced the product, 0 means it was
 * deliberately priced at zero - 3PL stock the warehouse holds but does not own. Both are legitimate,
 * they mean opposite things to a valuation, and a screen that showed an unpriced product as £0.00
 * would state that the goods are worth nothing. So they never share a rendering.
 *
 * Every method answers a string. That is not a stylistic choice: the grid blanks any falsy value it
 * is handed (`grid.component.ts:385`), so a numeric 0 returned from a column formatter renders as an
 * empty cell - indistinguishable from a value that failed to load.
 *
 * Comparisons go through StockQuantityService so one epsilon governs the module. Its round() is four
 * decimals, matching the backend's `roundCost`, which is what makes the tighter epsilon here
 * immaterial: both sides round to four places before testing, so they agree on what zero is.
 */
@Injectable({
  providedIn: 'root'
})
export class CostDisplayService {

  constructor(private stockQuantityService: StockQuantityService) { }

  /**
   * Whether there is no amount here at all, as opposed to an amount of zero.
   *
   * An empty string is treated as absent rather than coerced, because Number('') is 0 and would
   * turn "never priced" into "priced at nothing" - the exact conflation this class exists to avoid.
   */
  isNotPriced(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }

    return !Number.isFinite(Number(value));
  }

  /**
   * A unit rate. The ledger keeps four decimals because a price per tonne can genuinely run to
   * fractions of a penny, so the extra digits are shown when dropping them would change the figure
   * on screen, and hidden when it would not.
   */
  cost(value: any): string {
    if (this.isNotPriced(value)) {
      return NOT_PRICED;
    }

    return '£' + this.amount(this.stockQuantityService.round(value));
  }

  /** A signed amount, so a write-down reads as one at a glance rather than after subtraction. */
  signed(value: any): string {
    if (this.isNotPriced(value)) {
      return NOT_PRICED;
    }

    const amount = this.stockQuantityService.round(value);

    if (this.stockQuantityService.isZero(amount)) {
      return 'No change';
    }

    return (this.stockQuantityService.isNegative(amount) ? '-£' : '+£') + this.amount(Math.abs(amount));
  }

  /**
   * The movement from one cost to the next.
   *
   * Refuses to subtract when either end was unpriced. The difference between "unpriced" and £4 is not
   * £4, and printing one would invent a change nobody made and put a number on it. The two one-sided
   * cases are named instead, because both are real entries the cost route writes: a first-ever
   * pricing records a null previous cost, and clearing a price back to null records a null cost.
   */
  change(cost: any, previousCost: any): string {
    const costMissing = this.isNotPriced(cost);
    const previousMissing = this.isNotPriced(previousCost);

    if (costMissing && previousMissing) {
      return NOT_PRICED;
    }

    if (costMissing) {
      return 'Price cleared';
    }

    if (previousMissing) {
      return 'First price';
    }

    return this.signed(this.stockQuantityService.round(cost) - this.stockQuantityService.round(previousCost));
  }

  /**
   * Whether this entry reduced what the product costs, so a screen can mark it without re-deriving
   * the comparison. False for the one-sided entries: clearing a price is not a reduction to zero.
   */
  isReduction(cost: any, previousCost: any): boolean {
    if (this.isNotPriced(cost) || this.isNotPriced(previousCost)) {
      return false;
    }

    return this.stockQuantityService.isNegative(
      this.stockQuantityService.round(cost) - this.stockQuantityService.round(previousCost));
  }

  /**
   * Who made the change.
   *
   * `createdBy` is not validated against the user table and its house default is -1, so an absent
   * user is reported as unrecorded rather than attributed to anybody. A name invented for an audit
   * trail is worse than a missing one.
   */
  changedBy(record: any): string {
    const user = record ? record.user : null;

    if (user && (user.firstName || user.lastName)) {
      return this.escape(((user.firstName || '') + ' ' + (user.lastName || '')).trim());
    }

    const id = Number(record ? record.createdBy : NaN);

    if (!Number.isInteger(id) || id <= 0) {
      return 'Not recorded';
    }

    return 'User #' + id;
  }

  /** An operator's own words, so an empty note reads as empty rather than as a failure to load. */
  notes(value: any): string {
    if (value === null || value === undefined || String(value).trim() === '') {
      return 'None given';
    }

    return this.escape(String(value));
  }

  /**
   * The grid writes a cell through `bypassSecurityTrustHtml`, so anything a person typed reaches the
   * DOM as markup unless it is escaped here first. Notes are free text entered by an operator.
   */
  escape(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Two decimals unless the four the column keeps hold something the two would drop. */
  private amount(value: number): string {
    const pence = Math.round(value * 100) / 100;

    return this.stockQuantityService.isZero(value - pence) ? pence.toFixed(2) : value.toFixed(4);
  }
}
